import { useTranslation } from "react-i18next";
import type {
  CommentsSummaryResponse,
  SocialCommentHandlingStatusFilter,
  SocialCommentListItemDto,
  SocialCommentPlatformFilter,
  SocialPostListItemDto,
} from "@multi-chat/shared";
import { ErrorState } from "../../../components/common/ErrorState/index.js";
import { cn } from "../../../lib/utils.js";
import { ConversationRowSkeleton } from "../ConversationList/ConversationRowSkeleton.js";
import { InboxNoResults } from "../InboxEmptyStates.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import { useNowMs } from "../use-now-ms.js";
import { CommentRowItem } from "./CommentRowItem.js";
import { CommentsListHeader, type CommentsListHeaderFilters } from "./CommentsListHeader.js";
import { CommentsReconnectBanner } from "./CommentsReconnectBanner.js";
import type { CommentsViewMode } from "./comments-url-state.js";
import { PostGroupRowItem } from "./PostGroupRowItem.js";
import type { SocialCommentsListView } from "./use-social-comments-list.js";
import type { SocialPostsListView } from "./use-social-posts-list.js";

const LOAD_MORE_THRESHOLD_PX = 96;
const LIST_TIME_REFRESH_MS = 60_000;

export function CommentsListPane({
  summary,
  filters,
  commentsView,
  postsView,
  hasMore,
  loadingMore,
  refreshing,
  syncing,
  selectedCommentId,
  selectedPostId,
  onViewModeChange,
  onPlatformChange,
  onHandlingStatusChange,
  onSortChange,
  onSearchChange,
  onClearPostFilter,
  onSync,
  onSelectComment,
  onSelectPost,
  onLoadMore,
  onRetry,
}: {
  summary: CommentsSummaryResponse | null;
  filters: CommentsListHeaderFilters;
  commentsView: SocialCommentsListView;
  postsView: SocialPostsListView;
  hasMore: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  syncing: boolean;
  selectedCommentId: string | null;
  selectedPostId: string | null;
  onViewModeChange: (viewMode: CommentsViewMode) => void;
  onPlatformChange: (platform: SocialCommentPlatformFilter | undefined) => void;
  onHandlingStatusChange: (status: SocialCommentHandlingStatusFilter) => void;
  onSortChange: (sort: "activity_desc" | "activity_asc") => void;
  onSearchChange: (search: string | undefined) => void;
  onClearPostFilter: () => void;
  onSync: () => void;
  onSelectComment: (commentId: string) => void;
  onSelectPost: (postId: string) => void;
  onLoadMore: () => boolean;
  onRetry: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const nowMs = useNowMs(LIST_TIME_REFRESH_MS);
  const activeView = filters.viewMode === "posts" ? postsView : commentsView;

  function handleScroll(event: React.UIEvent<HTMLUListElement>): void {
    if (!hasMore || loadingMore) return;
    const el = event.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= LOAD_MORE_THRESHOLD_PX) {
      onLoadMore();
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <CommentsListHeader
        filters={filters}
        syncing={syncing}
        onViewModeChange={onViewModeChange}
        onPlatformChange={onPlatformChange}
        onHandlingStatusChange={onHandlingStatusChange}
        onSortChange={onSortChange}
        onSearchChange={onSearchChange}
        onClearPostFilter={onClearPostFilter}
        onSync={onSync}
      />

      {summary ? (
        <CommentsReconnectBanner
          permissions={summary.permissions}
          platformFilter={filters.platform}
        />
      ) : null}

      {activeView.kind === "loading" ? (
        <ConversationRowSkeleton label={t("inbox.comments.loading")} />
      ) : null}

      {activeView.kind === "error" ? (
        <div className="grid min-h-0 flex-1 place-items-center overflow-hidden bg-inbox-rows p-5">
          <ErrorState
            layout="panel"
            message={activeView.message}
            onRetry={onRetry}
            retryLabel={t("inbox.retry")}
          />
        </div>
      ) : null}

      {activeView.kind === "empty" ? (
        <CommentsEmpty viewMode={filters.viewMode} />
      ) : null}

      {activeView.kind === "searchEmpty" ? (
        <InboxNoResults
          actionLabel={t("inbox.comments.search.clear")}
          onAction={() => onSearchChange(undefined)}
        />
      ) : null}

      {activeView.kind === "ready" ? (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-inbox-rows">
          <ul
            className={cn(
              "m-0 flex min-h-0 flex-1 list-none flex-col overflow-y-auto overscroll-contain px-0 pt-1.5 pb-3.5",
              INBOX_SCROLLER_CLASS,
              refreshing && "opacity-85",
            )}
            role="listbox"
            aria-label={t("inbox.comments.listLabel")}
            aria-busy={refreshing || undefined}
            onScroll={handleScroll}
          >
            {filters.viewMode === "posts"
              ? (activeView.items as SocialPostListItemDto[]).map((item) => (
                  <li key={item.id} role="option" aria-selected={item.id === selectedPostId}>
                    <PostGroupRowItem
                      item={item}
                      selected={item.id === selectedPostId}
                      nowMs={nowMs}
                      onSelect={onSelectPost}
                    />
                  </li>
                ))
              : (activeView.items as SocialCommentListItemDto[]).map((item) => (
                  <li key={item.id} role="option" aria-selected={item.id === selectedCommentId}>
                    <CommentRowItem
                      item={item}
                      selected={item.id === selectedCommentId}
                      nowMs={nowMs}
                      onSelect={onSelectComment}
                    />
                  </li>
                ))}
            {loadingMore ? (
              <li className="px-4 py-2 text-center text-[0.75rem] text-inbox-muted">
                {t("inbox.loadingMoreConversations")}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function CommentsEmpty({ viewMode }: { viewMode: CommentsViewMode }): React.JSX.Element {
  const { t } = useTranslation();
  if (viewMode === "posts") {
    return (
      <div className="grid min-h-0 flex-1 place-items-center bg-inbox-rows p-6 text-center">
        <div className="max-w-[18rem]">
          <h2 className="m-0 text-[1.0625rem] font-semibold text-inbox-strong">
            {t("inbox.comments.empty.postsTitle")}
          </h2>
          <p className="m-0 mt-1.5 text-[0.84375rem] text-inbox-muted">
            {t("inbox.comments.empty.postsDescription")}
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="grid min-h-0 flex-1 place-items-center bg-inbox-rows p-6 text-center">
      <div className="max-w-[18rem]">
        <h2 className="m-0 text-[1.0625rem] font-semibold text-inbox-strong">
          {t("inbox.comments.empty.commentsTitle")}
        </h2>
        <p className="m-0 mt-1.5 text-[0.84375rem] text-inbox-muted">
          {t("inbox.comments.empty.commentsDescription")}
        </p>
      </div>
    </div>
  );
}
